import { useEffect, useRef, useState } from 'react'

import trackEvents from '../constants/track-events'
import { useAivyContext, usePartnerContext, useUserContext } from '../context'
import { notifyBugsnag } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import { localStorage } from '../helpers/local-storage'
import {
  SPACE_FALLBACK,
  canAutoSwitch,
  planSpaceFallback,
  recordAutoSwitch,
  rollbackAutoSwitch
} from '../helpers/space-auto-switch'
import { switchActiveSpace } from '../helpers/switch-active-space'
import { useSpacesAccess } from './use-spaces-access'

export const SPACE_FALLBACK_PHASE = Object.freeze({
  CHECKING: 'CHECKING',
  CHOOSE: 'CHOOSE',
  SWITCHING: 'SWITCHING',
  NONE: 'NONE'
})

// So lange wartet die Abgelaufen-Ansicht auf die Antwort. Länger soll ein
// Nutzer mit abgelaufenem Zugang nicht auf einen Indikator schauen. Danach
// gilt die bisherige Ansicht, spätere Antworten werden ignoriert.
export const CHECK_TIMEOUT_MS = 8000

/**
 * Entscheidet für die Abgelaufen-Ansicht, ob der Nutzer auf einen anderen
 * Standort mit gültigem Zugang wechseln kann. Phasen
 *
 * CHECKING: Antwort steht aus. SWITCHING: der automatische Wechsel läuft.
 * CHOOSE: Auswahl der nutzbaren Standorte. NONE: bisherige Ansicht, auch bei
 * jedem Fehler, bei Timeout und für alle ohne weiteren Standort.
 *
 * Die Entscheidung fällt genau einmal pro Mount. Wer die Ansicht verlässt und
 * wieder öffnet, wird durch den Stand in localStorage geschützt
 * (helpers/space-auto-switch.js), nicht durch diesen Hook.
 */
export const useSpaceFallback = () => {
  const { employees, user, activeSpace, isAdmin } = useUserContext()
  const { partner } = usePartnerContext()
  const { restartDashboard } = useAivyContext()

  // Aivy-Admins und die Impersonation (localStorage admin) haben eigene Spaces,
  // die nicht zum angezeigten Partner gehören. Wer nur einen Standort hat,
  // hat nichts zu wechseln und löst keinen Request aus.
  const enabled =
    !isAdmin && !localStorage.getItem('admin') && employees.length > 1

  const access = useSpacesAccess({ username: user.id, enabled })

  const [phase, setPhase] = useState(
    enabled ? SPACE_FALLBACK_PHASE.CHECKING : SPACE_FALLBACK_PHASE.NONE
  )
  const [candidates, setCandidates] = useState([])
  const [timedOut, setTimedOut] = useState(false)
  const decidedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return undefined

    const timer = setTimeout(() => setTimedOut(true), CHECK_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [enabled])

  useEffect(() => {
    if (!enabled || decidedRef.current) return

    const finish = (nextPhase, nextCandidates = []) => {
      decidedRef.current = true
      setCandidates(nextCandidates)
      setPhase(nextPhase)
    }

    if (access.isError || timedOut) {
      finish(SPACE_FALLBACK_PHASE.NONE)
      return
    }

    if (!access.isSuccess) return

    // Jede Ausnahme gleich bisherige Ansicht. Ein Wurf im Render der neuen
    // Pfade würde sonst über die ErrorBoundary das ganze Dashboard durch die
    // Wartungsseite ersetzen.
    try {
      const plan = planSpaceFallback({
        activeSpace,
        partnerId: partner.id,
        employees,
        spacesAccess: access.data,
        autoAllowed:
          document.visibilityState === 'visible' &&
          canAutoSwitch({ user: user.id, spaceId: activeSpace.id })
      })

      if (plan.kind === SPACE_FALLBACK.NONE) {
        finish(SPACE_FALLBACK_PHASE.NONE)
        return
      }

      if (plan.kind === SPACE_FALLBACK.CHOOSE) {
        finish(SPACE_FALLBACK_PHASE.CHOOSE, plan.candidates)
        return
      }

      const [{ space: target }] = plan.candidates

      // Den Guard direkt vor der Aktion erneut lesen, ein zweiter Tab kann
      // gerade gewechselt haben. Ohne schreibbaren Speicher kein Auto-Wechsel.
      const isRecorded =
        canAutoSwitch({ user: user.id, spaceId: activeSpace.id }) &&
        recordAutoSwitch({
          user: user.id,
          from: activeSpace.id,
          to: target.id,
          fromName: partner.name
        })

      if (!isRecorded) {
        finish(SPACE_FALLBACK_PHASE.CHOOSE, plan.candidates)
        return
      }

      finish(SPACE_FALLBACK_PHASE.SWITCHING, plan.candidates)

      trackEvent(trackEvents.SPACE_AUTO_SWITCH_ACCESS_EXPIRED, {
        from: activeSpace.id,
        to: target.id
      })

      switchActiveSpace({
        userId: user.id,
        spaceId: target.id,
        deliberate: false
      })
        .then(restartDashboard)
        .catch(() => {
          // Kein Reload, also keine Schleife. Der Cooldown würde den Nutzer
          // wegen eines Netzaussetzers 24 Stunden sperren.
          rollbackAutoSwitch({ user: user.id })
          setPhase(SPACE_FALLBACK_PHASE.CHOOSE)
        })
    } catch (error) {
      notifyBugsnag(error)
      finish(SPACE_FALLBACK_PHASE.NONE)
    }
  }, [
    enabled,
    timedOut,
    access.isError,
    access.isSuccess,
    access.data,
    activeSpace,
    partner,
    employees,
    user.id,
    restartDashboard
  ])

  return {
    phase: enabled ? phase : SPACE_FALLBACK_PHASE.NONE,
    candidates
  }
}
